import React, { useEffect, useState } from "react";
import { getData, postData, putData } from "../FatchApi/FatchApi";

import Spinner from "./Loader/Spinner";

const ProductAddForm = ({ id }) => {
  const initialFormData = {
    title: id.title ? id.title : "",
    metatitle: id.metatitle ? id.metatitle : "",
    productName: id.productName ? id.productName : "",
    productDescription: id.productDescription ? id.productDescription : "",
    metaDescription: id.metaDescription ? id.metaDescription : "",
    metaKeywords: id.metaKeywords ? id.metaKeywords : "",
    slug: id.slug ? id.slug : "",
    quantity: id.quantity ? id.quantity : "",
    images: id.images ? id.images : "",

    category:
      id.category?.length !== 0 ? id.category?.map((cat) => cat._id || cat) : [],
    mrp: id.mrp ? id.mrp : "",
    discount: id.discount ? id.discount : 0,
    sellingPrice: id.sellingPrice ? id.sellingPrice : "",
    hsn: id.hsn ? id.hsn : "",
    tax: id.tax ? id.tax : "",
    sku: id.sku ? id.sku : "",
    ean: id.EanCode ? id.EanCode : "",
    manufacturerDetails: id.manufacturerDetails ? id.manufacturerDetails : "",
    shelfLife: id.shelfLife ? id.shelfLife : "",
    length: id.length ? id.length : "",
    breadth: id.breadth ? id.breadth : "",
    height: id.height ? id.height : "",
    weight: id.weight ? id.weight : "",
    type: id.type ? id.type : "",
    ingredients: id.ingredients ? id.ingredients : "",
    nutrientContent: id.nutrientContent ? id.nutrientContent : "",
    offer: id.offer?._id || "66d2b5ea01f8e3d2d22501ee",
  };
  const [category, setCategory] = useState([]);
  const [offers, setOffers] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);
  const [productData, setProductData] = useState(initialFormData);
  const [sellingPrice, setSellingPrice] = useState(id?.sellingPrice);
  const [inStock, setInstock] = useState(id?.inStock);

  /**
   * Unified image list — each item is one of:
   *  { type: 'existing', src: fullUrl, filename: 'abc.jpg' }
   *  { type: 'new',      src: blobUrl, file: File }
   */
  const [images, setImages] = useState([]);

  const totalImages = images.length;

  // ─── Populate existing images in edit mode ───────────────────────────────
  useEffect(() => {
    if (id && id.images && id.images.length > 0) {
      const existingItems = id.images.map((url) => ({
        type: "existing",
        src: url,
        // extract just the filename from the URL for sending to backend
        filename: url.split("/").pop(),
      }));
      setImages(existingItems);
    }
  }, [id]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, options } = e.target;

    if (name === "category") {
      const selectedValues = Array.from(options)
        .filter((option) => option.selected)
        .map((option) => option.value);

      setProductData((prevData) => ({
        ...prevData,
        [name]: selectedValues,
      }));
    } else {
      const { value } = e.target;
      setProductData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const MAX_SIZE_MB = 1;

    if (totalImages + files.length > 10) {
      alert(`Maximum 10 images allowed. You currently have ${totalImages}.`);
      e.target.value = "";
      return;
    }

    const newItems = [];
    for (const file of files) {
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        alert(`File "${file.name}" exceeds 1MB and was skipped.`);
        continue;
      }
      newItems.push({
        type: "new",
        src: URL.createObjectURL(file),
        file,
      });
    }

    setImages((prev) => [...prev, ...newItems]);
    // Reset input so the same file can be re-selected if needed
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const moveImage = (index, direction) => {
    setImages((prev) => {
      const updated = [...prev];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex >= 0 && targetIndex < updated.length) {
        [updated[index], updated[targetIndex]] = [
          updated[targetIndex],
          updated[index],
        ];
      }
      return updated;
    });
  };

  const handleStock = () => {
    setInstock(!inStock);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (totalImages < 3) {
      alert("Please add at least 3 product images before saving.");
      return;
    }

    setShowSpinner(true);
    const contanttype = "";
    const formData = new FormData();
    formData.append("title", productData.title);
    formData.append("metatitle", productData.metatitle);
    formData.append("productDescription", productData.productDescription);
    formData.append("metaDescription", productData.metaDescription);
    formData.append("metaKeywords", productData.metaKeywords);
    formData.append("slug", productData?.slug);
    formData.append("productName", productData.productName);
    formData.append("quantity", productData.quantity);
    formData.append("inStock", inStock);
    formData.append("mrp", productData.mrp);
    formData.append("discount", productData.discount);
    formData.append("sku", productData.sku);
    formData.append("tax", productData.tax);
    formData.append("hsn", productData.hsn);
    formData.append("EanCode", productData.ean);
    formData.append("sellingPrice", sellingPrice);
    formData.append("manufacturerDetails", productData.manufacturerDetails);
    formData.append("shelfLife", productData.shelfLife);
    formData.append("length", productData.length);
    formData.append("breadth", productData.breadth);
    formData.append("height", productData.height);
    formData.append("weight", productData.weight);
    formData.append("type", productData.type);
    formData.append("ingredients", productData.ingredients);
    formData.append("nutrientContent", productData.nutrientContent);
    formData.append("offer", productData.offer);

    // ── Separate existing filenames from new File objects ──
    images.forEach((item) => {
      if (item.type === "existing") {
        formData.append("existingImages", item.filename);
      } else if (item.type === "new") {
        formData.append("images", item.file);
      }
    });

    productData.category?.forEach((category) => {
      formData.append("category", category);
    });

    if (id && id._id) {
      // EDIT
      const Id = id._id;
      const res = await putData(`api/products/update-products/${Id}`, {
        data: formData,
        contanttype,
      });

      if (res && res.status === "success") {
        window.location.reload();
      } else {
        setShowSpinner(false);
        alert(res?.message || "Update failed. Please try again.");
      }
    } else {
      // ADD NEW
      const res = await postData("api/products/add-products", {
        data: formData,
        contanttype,
      });
      if (res && res.status === "success") {
        window.location.reload();
      } else {
        setShowSpinner(false);
        alert(res?.message || "Failed to add product. Please try again.");
      }
    }
  };

  const getDynamicData = async () => {
    const cat = await getData("api/category/get-category");
    const offers = await getData("api/offer/get-offers");
    setCategory(cat.category);
    setOffers(offers.offers);
  };

  useEffect(() => {
    getDynamicData();
  }, []);

  return (
    <>
      <div className="relative min-h-screen">
        {showSpinner ? <Spinner /> : " "}
        <form
          encType="multipart/form-data"
          onSubmit={handleSubmit}
          className="grid grid-cols-2 gap-4 mx-auto"
        >
          <div className="mb-4">
            <label htmlFor="category" className="block mb-1 font-medium">
              Category
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="category"
              name="category"
              value={productData.category}
              onChange={handleChange}
              required
              multiple
            >
              <option value="">Select Product Category</option>
              {category.map((item) => (
                <option
                  key={item._id}
                  value={item._id}
                  selected={initialFormData?.category == item._id}
                >
                  {item?.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="name" className="block mb-1 font-medium">
              Product Name
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="productName"
              name="productName"
              placeholder="Enter product name"
              value={productData.productName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="title" className="block mb-1 font-medium">
              Title
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="title"
              placeholder="Enter product title"
              name="title"
              value={productData.title}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="title" className="block mb-1 font-medium">
              Meta Title
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="metatitle"
              name="metatitle"
              placeholder="Enter meta title"
              value={productData.metatitle}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="productDescription"
              className="block mb-1 font-medium"
            >
              Product Description
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded bg-light"
              rows="3"
              placeholder="Enter product description"
              id="productDescription"
              name="productDescription"
              value={productData.productDescription}
              onChange={handleChange}
              required
            ></textarea>
          </div>
          <div className="mb-4">
            <label
              htmlFor="productDescription"
              className="block mb-1 font-medium"
            >
              Meta Description
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded bg-light"
              rows="3"
              placeholder="Enter meta description"
              id="metaDescription"
              name="metaDescription"
              value={productData.metaDescription}
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="mb-4">
            <label htmlFor="metaKeywords" className="block mb-1 font-medium">
              Meta Keywords
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="metaKeywords"
              name="metaKeywords"
              value={productData.metaKeywords}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="metaKeywords" className="block mb-1 font-medium">
              Product URL
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="slug"
              name="slug"
              placeholder="product-url-format"
              disabled={id.slug ? true : false}
              value={productData.slug}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label htmlFor="quantity" className="block mb-1 font-medium">
              Quantity
            </label>
            <input
              type="number"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter quantity(50)"
              id="quantity"
              name="quantity"
              value={productData.quantity >= 0 ? productData.quantity : ""}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="mrp" className="block mb-1 font-medium">
              MRP
            </label>
            <input
              type="number"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter MRP Rs"
              id="mrp"
              name="mrp"
              value={productData.mrp >= 0 ? productData.mrp : ""}
              onChange={(e) => {
                handleChange(e);
                setSellingPrice(
                  Math.floor(
                    e.target.value - (e.target.value * productData.discount) / 100
                  )
                );
              }}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="mrp" className="block mb-1 font-medium">
              Discount %
            </label>
            <input
              type="number"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="0"
              id="discount"
              name="discount"
              value={productData.discount}
              onChange={(e) => {
                handleChange(e);
                setSellingPrice(
                  Math.floor(
                    productData.mrp - (productData?.mrp * e.target.value) / 100
                  )
                );
              }}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="sellingPrice" className="block mb-1 font-medium">
              Selling Price
            </label>
            <input
              type="number"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter selling price Rs"
              id="sellingPrice"
              name="sellingPrice"
              value={sellingPrice}
              onChange={(e) => setSellingPrice(e.target.value)}
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="sku" className="block mb-1 font-medium">
              SKU
            </label>
            <input
              type="text"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter SKU (PRA8765)"
              id="sku"
              name="sku"
              value={productData.sku}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="tax" className="block mb-1 font-medium">
              TAX
            </label>
            <select
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter Tax"
              id="tax"
              name="tax"
              value={productData.tax}
              onChange={handleChange}
              required
            >
              <option selected>Select One</option>
              <option value={0}>GST_0</option>
              <option value={12}>GST_12</option>
              <option value={18}>GST_18</option>
              <option value={28}>GST_28</option>
              <option value={3}>GST_3</option>
              <option value={5}>GST_5</option>
              <option value={"GST_APPAREL"}>GST_APPAREL</option>
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="mrp" className="block mb-1 font-medium">
              HSN
            </label>
            <input
              type="number"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter HSN(76543567)"
              id="hsn"
              name="hsn"
              value={productData.hsn}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="mrp" className="block mb-1 font-medium">
              EAN Code
            </label>
            <input
              type="number"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter Ean(87654589) "
              id="ean"
              name="ean"
              value={productData.ean}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="manufacturerDetails"
              className="block mb-1 font-medium"
            >
              Manufacturer Details
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="manufacturerDetails"
              name="manufacturerDetails"
              placeholder="Enter manufacturer details "
              value={productData.manufacturerDetails}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="shelfLife" className="block mb-1 font-medium">
              Shelf Life
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="shelfLife"
              name="shelfLife"
              placeholder="Enter shel life"
              value={productData.shelfLife}
              onChange={handleChange}
              required
            />
          </div>

          <div className="mb-4">
            <label htmlFor="weight" className="block mb-1 font-medium">
              Weight in (g)
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="weight"
              name="weight"
              placeholder="Enter product weight(500g)"
              value={productData.weight}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="type" className="block mb-1 font-medium">
              Type
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="type"
              name="type"
              placeholder="Prodcut type"
              value={productData.type}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="ingredients" className="block mb-1 font-medium">
              Ingredients
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="ingredients"
              name="ingredients"
              placeholder="Enter ingredients"
              value={productData.ingredients}
              onChange={handleChange}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="nutrientContent" className="block mb-1 font-medium">
              Nutrient Content
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              id="nutrientContent"
              name="nutrientContent"
              placeholder="Enter nutrient content"
              value={productData.nutrientContent}
              onChange={handleChange}
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="tax"
              className="block mb-1 font-medium text-red-600"
            >
              Add Offer
            </label>
            <select
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded"
              placeholder="Enter Tax"
              id="offer"
              name="offer"
              value={productData.offer}
              onChange={handleChange}
              required
            >
              <option selected>Select One</option>
              {offers?.map((data) => (
                <option
                  key={data?._id}
                  value={data?._id}
                  selected={data?._id == productData.offer}
                >
                  {data?.type}
                </option>
              ))}
            </select>
          </div>

          {/* ─── Product Images Section ─── */}
          <div className="mb-4 col-span-2">
            <label htmlFor="images" className="block mb-2 font-medium text-lg">
              Product Images{" "}
              <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Recommended: 400×500px (4:5 portrait), &lt;1MB, AVIF/WebP
              </span>
            </label>
            <input
              type="file"
              id="images"
              name="images_input"
              onChange={handleFileChange}
              multiple
              accept="image/*"
              className="w-full px-3 py-2 border border-gray-300 rounded mb-4"
            />

            {/* Count + warnings */}
            <div className="flex justify-between items-center mb-2">
              <span
                className={`font-bold text-lg ${
                  totalImages < 3 ? "text-red-600" : "text-green-600"
                }`}
              >
                {totalImages}/10 images
                {totalImages < 3 && " — Minimum 3 required"}
              </span>
              {totalImages > 0 && (
                <span className="text-sm text-gray-500">
                  Hover to reorder (↑ ↓) or remove (×)
                </span>
              )}
            </div>

            {/* Preview Gallery */}
            {images.length > 0 && (
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-96 overflow-y-auto p-2 border rounded-lg bg-gray-50">
                  {images.map((item, index) => (
                    <div
                      key={index}
                      className="relative group bg-white rounded-lg shadow-md hover:shadow-xl transition-all p-1"
                    >
                      {/* Badge for existing vs new */}
                      <span
                        className={`absolute top-1 left-1 z-10 text-[10px] px-1 rounded font-bold ${
                          item.type === "existing"
                            ? "bg-green-500 text-white"
                            : "bg-blue-500 text-white"
                        }`}
                      >
                        {item.type === "existing" ? "Saved" : "New"}
                      </span>
                      <img
                        src={item.src}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 md:h-28 object-cover rounded cursor-move"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all rounded-lg">
                        <div className="flex space-x-1">
                          <button
                            type="button"
                            onClick={() => moveImage(index, "up")}
                            disabled={index === 0}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded disabled:opacity-40"
                            title="Move Left"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveImage(index, "down")}
                            disabled={index === images.length - 1}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded disabled:opacity-40"
                            title="Move Right"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="bg-red-500 hover:bg-red-600 text-white text-xs px-2 py-1 rounded"
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {totalImages < 3 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    ⚠️ Minimum 3 images required for product listing
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center mb-4">
            <input
              id="default-checkbox"
              type="checkbox"
              checked={inStock}
              onChange={handleStock}
              className="w-6 h-6 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label
              htmlFor="default-checkbox"
              className="ms-4 text-lg font-medium text-green-600 dark:text-green-400"
            >
              InStock
            </label>
          </div>
          <div className="text-center col-span-2">
            <button
              type="submit"
              disabled={totalImages < 3}
              className={`px-8 py-3 text-white font-bold rounded-lg text-lg transition-all w-full ${
                totalImages < 3
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600 hover:shadow-lg hover:scale-[1.02]"
              }`}
            >
              {totalImages < 3
                ? `Add ${3 - totalImages} more image${3 - totalImages === 1 ? "" : "s"} (Min 3 required)`
                : `Save Product`}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default ProductAddForm;
